import React from 'react';
import { EventModel } from './useEventManagement';
import { FormField, SelectField } from './EventFormSteps';
import { AdminModal } from '../../pages/admin/ConfirmationDialogs';

const EventModals: React.FC<{ model: EventModel }> = ({ model }) => {
  const {
    sponsorForm, setSponsorForm, waypointForm, setWaypointForm,
    showSponsorForm, setShowSponsorForm, showWaypointForm, setShowWaypointForm,
    cancelConfirm, setCancelConfirm, cancelReason, setCancelReason,
    deleteConfirm, setDeleteConfirm, handleAddSponsor, handleAddWaypoint,
    handleCancel, handleDelete, inputClass, inputStyle,
  } = model;

  const renderFormField = (label: string, children: React.ReactNode) => (
    <FormField label={label}>{children}</FormField>
  );
  const renderSelectField = (label: string, value: string, options: { value: string; label: string }[], onChange: (v: string) => void) => (
    <SelectField label={label} value={value} options={options} onChange={onChange} />
  );

  return (
    <>
      {showSponsorForm && (
        <AdminModal ariaLabel="Add sponsor" onCancel={() => setShowSponsorForm(false)} maxWidth="28rem">
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--rp-text)' }}>Add Sponsor</h3>
            <div className="space-y-3">
              {renderFormField('Name *',
                <input type="text" value={sponsorForm.name} onChange={e => setSponsorForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Sponsor name" className={inputClass} style={inputStyle} />
              )}
              {renderFormField('Website URL',
                <input type="url" value={sponsorForm.website_url} onChange={e => setSponsorForm(f => ({ ...f, website_url: e.target.value }))}
                  placeholder="https:// (optional)" className={inputClass} style={inputStyle} />
              )}
              {renderSelectField('Tier', sponsorForm.tier,
                [{ value: 'title', label: 'Title — Presented By' }, { value: 'partner', label: 'Partner — In Partnership With' }, { value: 'supporter', label: 'Supporter — Supported By' }],
                v => setSponsorForm(f => ({ ...f, tier: v })))}
              {sponsorForm.tier === 'title' && renderFormField('Tagline',
                <input type="text" value={sponsorForm.tagline} onChange={e => setSponsorForm(f => ({ ...f, tagline: e.target.value }))}
                  placeholder="Sponsor tagline (optional)" className={inputClass} style={inputStyle} />
              )}
              {renderFormField('Color',
                <input type="color" value={sponsorForm.fallback_color} onChange={e => setSponsorForm(f => ({ ...f, fallback_color: e.target.value }))}
                  className="w-full h-8 rounded cursor-pointer" />
              )}
            </div>
            <div className="flex gap-3 justify-end mt-4">
              <button onClick={() => setShowSponsorForm(false)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--rp-text-secondary)' }}>Cancel</button>
              <button onClick={handleAddSponsor} className="admin-btn admin-btn-primary">Add Sponsor</button>
            </div>
        </AdminModal>
      )}

      {/* Add Waypoint Modal */}
      {showWaypointForm && (
        <AdminModal ariaLabel="Add waypoint" onCancel={() => setShowWaypointForm(false)} maxWidth="28rem">
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--rp-text)' }}>Add Waypoint</h3>
            <div className="space-y-3">
              {renderFormField('Name *',
                <input type="text" value={waypointForm.name} onChange={e => setWaypointForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Waypoint name" className={inputClass} style={inputStyle} />
              )}
              <div className="grid grid-cols-2 gap-3">
                {renderFormField('Latitude *',
                  <input type="number" step="any" value={waypointForm.latitude} onChange={e => setWaypointForm(f => ({ ...f, latitude: e.target.value }))}
                    placeholder="0.0" className={inputClass} style={inputStyle} />
                )}
                {renderFormField('Longitude *',
                  <input type="number" step="any" value={waypointForm.longitude} onChange={e => setWaypointForm(f => ({ ...f, longitude: e.target.value }))}
                    placeholder="0.0" className={inputClass} style={inputStyle} />
                )}
              </div>
              {renderSelectField('Type', waypointForm.waypoint_type,
                [{ value: 'waypoint', label: 'Waypoint' }, { value: 'gas_station', label: 'Gas Station' }, { value: 'restaurant', label: 'Restaurant' },
                 { value: 'scenic_view', label: 'Scenic View' }, { value: 'photo_stop', label: 'Photo Stop' }, { value: 'rest_area', label: 'Rest Area' }],
                v => setWaypointForm(f => ({ ...f, waypoint_type: v })))}
              {renderFormField('Description',
                <input type="text" value={waypointForm.description} onChange={e => setWaypointForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional description" className={inputClass} style={inputStyle} />
              )}
            </div>
            <div className="flex gap-3 justify-end mt-4">
              <button onClick={() => setShowWaypointForm(false)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--rp-text-secondary)' }}>Cancel</button>
              <button onClick={handleAddWaypoint} className="admin-btn admin-btn-primary">Add Waypoint</button>
            </div>
        </AdminModal>
      )}

      {/* Cancel Confirmation Modal */}
      {cancelConfirm && (
        <AdminModal ariaLabel="Cancel event" onCancel={() => setCancelConfirm(null)} maxWidth="28rem">
            <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--rp-text)' }}>Cancel Event?</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--rp-text-secondary)' }}>This will cancel the event and notify all participants.</p>
            {renderFormField('Reason',
              <input type="text" value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                placeholder="Cancellation reason..." className={inputClass} style={inputStyle} />
            )}
            <div className="flex gap-3 justify-end mt-4">
              <button onClick={() => setCancelConfirm(null)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--rp-text-secondary)' }}>Back</button>
              <button onClick={handleCancel} className="admin-btn" style={{ backgroundColor: 'var(--admin-warning-strong)', color: '#fff' }}>Cancel Event</button>
            </div>
        </AdminModal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <AdminModal ariaLabel="Delete event" onCancel={() => setDeleteConfirm(null)} maxWidth="28rem">
            <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--rp-text)' }}>Delete Event?</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--rp-text-secondary)' }}>This will soft-delete the event. It will no longer appear in lists or the app.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--rp-text-secondary)' }}>Back</button>
              <button onClick={handleDelete} className="admin-btn admin-btn-danger">Delete Event</button>
            </div>
        </AdminModal>
      )}
    </>
  );
};

export default EventModals;
